import React, { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import styles from '../products.module.scss';
import cx from 'classname';
import {
  AssociatedFieldIcon,
  DateIcon,
  DropdownIcon,
  EmailIcon,
  EmptyChartIcon,
  FileIcon,
  ImageIcon,
  LongTextIcon,
  MoveIcon,
  NumberIcon,
  SettingsIcon,
  ShortTextIcon,
} from 'components/icons';
import FormBuilderSettings from '../FormBuilderSettings';
import PhonePreview from '../Preview';
import { useDispatch } from 'react-redux';
import { showToast } from 'store/actions/toast';
import Button from 'components/button';
import FirebaseAnalytics, { AnalyticsLogStatus } from 'libs/analytics';
import { lgaFileId } from 'data';

// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
  if (!list) return;
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};
/**
 * Moves an item from one list to another list.
 */
const copy = (
  source,
  destination,
  droppableSource,
  droppableDestination,
  type,
) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const item = sourceClone[droppableSource.index];

  if (type && type === 'quick') {
    destClone.splice(droppableDestination.index, 0, {
      ...item,
      value: '',
      image: <ShortTextIcon />,
      property: 'text',
      hint: '',
      name: 'Name',
      required: false,
      id: uuid(),
      type: 'SHORT_TEXT',
      lookupRequired: false,
    });
    destClone.splice(droppableDestination.index, 0, {
      ...item,
      value: '',
      image: <LongTextIcon />,
      property: 'number',
      hint: '',
      name: 'Phone Number',
      required: false,
      id: uuid(),
      type: 'NUMBER',
      lookupRequired: false,
    });
    destClone.splice(droppableDestination.index, 0, {
      ...item,
      value: '',
      image: <EmailIcon />,
      property: 'email',
      hint: '',
      name: 'Email',
      required: false,
      id: uuid(),
      type: 'EMAIL',
      lookupRequired: false,
    });
    return destClone;
  } else if (type && type === 'address') {
    destClone.splice(droppableDestination.index, 0, {
      ...item,
      value: '',
      image: <LongTextIcon />,
      property: 'text',
      hint: '',
      name: 'Address',
      required: false,
      id: uuid(),
      type: 'LONG_TEXT',
      lookupRequired: false,
    });
    destClone.splice(droppableDestination.index, 0, {
      ...item,
      value: '',
      image: <DateIcon />,
      property: 'date',
      hint: '',
      name: 'Date',
      required: false,
      id: uuid(),
      type: 'DATE',
      lookupRequired: false,
    });
    return destClone;
  } else if (type && type === 'lga') {
    destClone.splice(droppableDestination.index, 0, {
      ...item,
      id: uuid(),
      value: 'State & LGA',
      image: <AssociatedFieldIcon />,
      property: 'text',
      hint: 'State & LGA',
      name: 'State & LGA',
      required: false,
      type: 'ASSOCIATED_FIELD',
      lookupRequired: false,
      associatedFieldExcelId: lgaFileId,
    });
    return destClone;
  }

  destClone.splice(droppableDestination.index, 0, { ...item, id: uuid() });
  return destClone;
};

const move = (source, destination, droppableSource, droppableDestination) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  destClone.splice(droppableDestination.index, 0, removed);

  const result = {};
  result[droppableSource.droppableId] = sourceClone;
  result[droppableDestination.droppableId] = destClone;

  return result;
};

const ITEMS = [
  {
    id: uuid(),
    value: '',
    image: <ShortTextIcon />,
    property: 'text',
    hint: '',
    name: 'Short Text',
    required: false,
    type: 'SHORT_TEXT',
    lookupRequired: false,
  },
  {
    id: uuid(),
    value: '',
    image: <LongTextIcon />,
    property: 'text',
    hint: '',
    name: 'Long Text',
    required: false,
    type: 'LONG_TEXT',
    lookupRequired: false,
  },
  {
    id: uuid(),
    value: '',
    image: <EmailIcon />,
    property: 'email',
    type: 'email',
    hint: '',
    name: 'Email',
    required: false,
    type: 'EMAIL',
    lookupRequired: false,
  },
  {
    id: uuid(),
    value: '',
    image: <NumberIcon />,
    property: 'number',
    hint: '',
    name: 'Number',
    max: '',
    required: false,
    type: 'NUMBER',
    lookupRequired: false,
  },
  {
    id: uuid(),
    value: '',
    image: <DateIcon />,
    property: 'date',
    hint: '',
    name: 'Date',
    required: false,
    type: 'DATE',
    lookupRequired: false,
  },
  {
    id: uuid(),
    value: '',
    image: <ImageIcon />,
    property: 'file',
    hint: '',
    name: 'Image',
    required: false,
    type: 'IMAGE',
    lookupRequired: false,
  },
  {
    id: uuid(),
    value: '',
    image: <FileIcon />,
    property: 'file',
    hint: '',
    name: 'File Upload',
    required: false,
    type: 'DOCUMENT',
    lookupRequired: false,
  },
  {
    id: uuid(),
    value: '',
    image: <DropdownIcon />,
    property: 'text',
    hint: '',
    name: 'Dropdown',
    subFields: '',
    required: false,
    type: 'DROPDOWN',
    lookupRequired: false,
    allowMultiSelect: false,
  },
  {
    id: uuid(),
    value: 'Associated Field',
    image: <AssociatedFieldIcon />,
    property: 'text',
    hint: 'Associated Field',
    name: 'Associated Field',
    required: false,
    type: 'ASSOCIATED_FIELD',
    lookupRequired: false,
    associatedFieldExcelId: '',
  },
];

const QUICK = [
  {
    id: uuid(),
    value: '',
    image: <ShortTextIcon />,
    property: 'text',
    hint: '',
    name: 'Name',
    required: false,
    type: 'SHORT_TEXT',
    lookupRequired: false,
  },
];

const ADDRESS = [
  {
    id: uuid(),
    value: '',
    image: <LongTextIcon />,
    property: 'text',
    hint: '',
    name: 'Address',
    required: false,
    type: 'LONG_TEXT',
    lookupRequired: false,
  },
];

const LGA = [
  {
    id: uuid(),
    value: 'State & LGA',
    image: <AssociatedFieldIcon />,
    property: 'text',
    hint: 'State & LGA',
    name: 'State & LGA',
    required: false,
    type: 'ASSOCIATED_FIELD',
    lookupRequired: false,
    associatedFieldExcelId: lgaFileId,
  },
];

const nameToLabel = {
  SHORT_TEXT: 'Short Text',
  LONG_TEXT: 'Long Text',
  EMAIL: 'Email',
  NUMBER: 'Number',
  DATE: 'Date',
  IMAGE: 'Image',
  DOCUMENT: 'Document',
  DROPDOWN: 'Dropdown',
  ASSOCIATED_FIELD: 'Associated Field',
  CUSTOMER_PHONE_NUMBER: 'Phone Number',
};

const nameToIcon = {
  SHORT_TEXT: <ShortTextIcon />,
  LONG_TEXT: <LongTextIcon />,
  EMAIL: <EmailIcon />,
  NUMBER: <NumberIcon />,
  DATE: <DateIcon />,
  IMAGE: <ImageIcon />,
  DOCUMENT: <FileIcon />,
  DROPDOWN: <DropdownIcon />,
  ASSOCIATED_FIELD: <AssociatedFieldIcon />,
  CUSTOMER_PHONE_NUMBER: <NumberIcon />,
};

const StepFour = ({ changeStep, data, setData }) => {
  const [state, setState] = useState({ [uuid()]: data.collectionFields });
  const [fieldEditDrawer, setFieldEditDrawer] = useState(false);
  const [currentFiedType, setCurrentFieldType] = useState({});
  const dispatch = useDispatch();

  const updateFieldType = (type) => {
    setCurrentFieldType(type);
    setFieldEditDrawer(true);
  };

  const updateFieldTypeItem = (type, item) => {
    const id = Object.keys(state)[0];
    const fields = state[id].map((field) => {
      if (field.id === item.id) {
        return {
          ...field,
          ...item,
        };
      }
      return field;
    });
    if (type !== 'no') {
      setFieldEditDrawer(false);
    }
    setState({ id: fields });
    const newData = fields.map((res, i) => {
      return {
        id: res.id,
        name: res.value,
        value: res.value,
        hint: res.hint,
        type: res.type,
        required: res.required,
        rank: i + 1,
        lookupRequired: res.lookupRequired,
        ...(res.type === 'DROPDOWN' && {
          allowMultiSelect: res.allowMultiSelect,
        }),
        ...(res.type === 'NUMBER' && {
          max: res.max,
        }),
        ...(res.type === 'DROPDOWN' &&
          res.options && {
            subFields: res?.options
              .trim()
              .split(/\s+/)
              .map((res) => {
                return {
                  name: res,
                  type: 'SHORT_TEXT',
                  value: res,
                };
              }),
          }),
        ...(res.type === 'DROPDOWN' &&
          !res.options && {
            subFields: res?.subFields,
          }),
        ...(res.type === 'DOCUMENT' && {
          allowMultiSelect: false,
          documentTypes: [res.documentTypes],
        }),
        ...(res.type === 'ASSOCIATED_FIELD' && {
          associatedFieldExcelId: res.associatedFieldExcelId,
        }),
      };
    });
    setData({ ...data, collectionFields: newData });
  };

  const handleDeleteField = (fieldId) => {
    const id = Object.keys(state)[0];
    let newField = {};
    const fields = state[id].filter((field) => field.id !== fieldId);
    setState({ id: fields });
    setFieldEditDrawer(false);
    setCurrentFieldType(newField);
    const newData = fields.map((res, i) => {
      return {
        id: res.id,
        name: res.value,
        hint: res.hint,
        type: res.type,
        required: res.required,
        rank: i + 1,
        lookupRequired: res.lookupRequired,
        ...(res.type === 'DROPDOWN' && {
          allowMultiSelect: res.allowMultiSelect,
        }),
        ...(res.type === 'NUMBER' && {
          max: res.max,
        }),
        ...(res.type === 'DROPDOWN' &&
          res.options && {
            subFields: res?.options
              .trim()
              .split(/\s+/)
              .map((res) => {
                return {
                  name: res,
                  type: 'SHORT_TEXT',
                  value: res,
                };
              }),
          }),
        ...(res.type === 'DROPDOWN' &&
          !res.options && {
            subFields: res?.subFields,
          }),
        ...(res.type === 'DOCUMENT' && {
          allowMultiSelect: false,
          documentTypes: res.documentTypes,
        }),
        ...(res.type === 'ASSOCIATED_FIELD' && {
          associatedFieldExcelId: res.associatedFieldExcelId,
        }),
      };
    });
    setData({ ...data, collectionFields: newData });
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;

    // dropped outside the list
    if (!destination) {
      return;
    }

    switch (source.droppableId) {
      case destination.droppableId:
        setState({
          [destination.droppableId]: reorder(
            state[source.droppableId],
            source.index,
            destination.index,
          ),
        });
        break;
      case 'ITEMS':
        setState({
          [destination.droppableId]: copy(
            ITEMS,
            state[destination.droppableId],
            source,
            destination,
          ),
        });
        break;
      case 'QUICK':
        setState({
          [destination.droppableId]: copy(
            QUICK,
            state[destination.droppableId],
            source,
            destination,
            'quick',
          ),
        });
        break;
      case 'ADDRESS':
        setState({
          [destination.droppableId]: copy(
            ADDRESS,
            state[destination.droppableId],
            source,
            destination,
            'address',
          ),
        });
        break;
      case 'LGA':
        setState({
          [destination.droppableId]: copy(
            LGA,
            state[destination.droppableId],
            source,
            destination,
            'lga',
          ),
        });
        break;
      default:
        setState(
          move(
            state[source.droppableId],
            state[destination.droppableId],
            source,
            destination,
          ),
        );
        break;
    }
  };

  const onContinue = () => {
    const findPhone = data?.collectionFields.findIndex(function (post) {
      if (post?.type === 'CUSTOMER_PHONE_NUMBER' && post?.value && post?.hint)
        return true;
    });
    if (findPhone === -1) {
      dispatch(
        showToast(
          'error',
          'Oops!',
          'Please fill in the label and hint  text for the  phone number field',
        ),
      );
    } else {
      changeStep(5);
    }
    FirebaseAnalytics.logWithValue('Add_collections_details', {
      STATUS: AnalyticsLogStatus.SUCCESS,
    });
  };

  return (
    <div className={styles.chooseBody}>
      <div className={styles.magCreate}>
        <p className={styles.chooseTitle}>Create Product Details</p>
        <div className={styles.createMsgs}>
          <p className={styles.chooseSub}>
            Create your product specifications and payment structure.
          </p>
          <div className={styles.btns}>
            {/* <Button
              className={styles.skipBtn}
              onClick={() => {
                changeStep(5);
                setData({ ...data, onboardingFields: [] });
              }}
              text="   Skip this step"
              variant="plain"
            /> */}
            {/* <Button
              text="Save as Draft"
              variant="gray"
              className={styles.draftsBtn}
            /> */}

            <Button
              className={styles.createBtn2}
              onClick={onContinue}
              variant="primary"
              text="Continue"
            />
          </div>
        </div>
      </div>
      <hr className={styles.line} />

      <DragDropContext onDragEnd={onDragEnd}>
        <div className={styles.rowCreate}>
          <div className={cx(styles.columnCreate, styles.leftCreate)}>
            <div className={styles.rowCreate}>
              <div
                className={cx(
                  styles.columnCreate,
                  styles.dragLeft,
                  styles.scrollStep2,
                )}
              >
                <p className={styles.description}>
                  Drag and drop these items below to build your form
                </p>
                <Droppable droppableId="ITEMS" isDropDisabled={true}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      isDraggingOver={snapshot.isDraggingOver}
                    >
                      {ITEMS.map((item, index) => (
                        <Draggable
                          key={item.id}
                          draggableId={item.id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <>
                              <div
                                className={styles.dragItem}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                isDragging={snapshot.isDragging}
                                style={provided.draggableProps.style}
                              >
                                {item.image}
                                <p>{item.name}</p>
                              </div>
                            </>
                          )}
                        </Draggable>
                      ))}
                    </div>
                  )}
                </Droppable>
                <p className={styles.productTitle}>Quick Add</p>

                <p className={styles.description}>
                  Drag these fields, but open them to customize them
                </p>
                <Droppable droppableId="QUICK" isDropDisabled={true}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      isDraggingOver={snapshot.isDraggingOver}
                    >
                      {QUICK.map((item, index) => (
                        <Draggable
                          key={item.id}
                          draggableId={item.id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <>
                              <div
                                className={styles.dragItem3}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                isDragging={snapshot.isDragging}
                                style={provided.draggableProps.style}
                              >
                                <div className={styles.dragOthers}>
                                  Name, Phone Number & Email
                                </div>
                              </div>
                            </>
                          )}
                        </Draggable>
                      ))}
                    </div>
                  )}
                </Droppable>

                <Droppable droppableId="ADDRESS" isDropDisabled={true}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      isDraggingOver={snapshot.isDraggingOver}
                    >
                      {ADDRESS.map((item, index) => (
                        <Draggable
                          key={item.id}
                          draggableId={item.id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <>
                              <div
                                className={styles.dragItem3}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                isDragging={snapshot.isDragging}
                                style={provided.draggableProps.style}
                              >
                                <p
                                  className={cx(
                                    styles.dragOthers,
                                    styles.dragOthers2,
                                  )}
                                >
                                  Address & Date
                                </p>
                              </div>
                            </>
                          )}
                        </Draggable>
                      ))}
                    </div>
                  )}
                </Droppable>

                <Droppable droppableId="LGA" isDropDisabled={true}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      isDraggingOver={snapshot.isDraggingOver}
                    >
                      {LGA.map((item, index) => (
                        <Draggable
                          key={item.id}
                          draggableId={item.id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <>
                              <div
                                className={styles.dragItem3}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                isDragging={snapshot.isDragging}
                                style={provided.draggableProps.style}
                              >
                                <p
                                  className={cx(
                                    styles.dragOthers,
                                    styles.dragOthers2,
                                  )}
                                >
                                  State & LGA
                                </p>
                              </div>
                            </>
                          )}
                        </Draggable>
                      ))}
                    </div>
                  )}
                </Droppable>
              </div>
              <div
                className={cx(
                  styles.columnCreate,
                  styles.dragRight,
                  styles.leftBg2,
                  styles.scrollStep2,
                )}
              >
                <div>
                  {Object.keys(state).map((list, i) => (
                    <Droppable key={list} droppableId={list}>
                      {(provided, snapshot) => (
                        <div
                          className={styles.dragList}
                          ref={provided.innerRef}
                          isDraggingOver={snapshot.isDraggingOver}
                        >
                          {state[list] && state[list].length ? (
                            state[list].map((item, index) => (
                              <Draggable
                                key={item.id}
                                draggableId={item.id}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <div className={styles.drag}>
                                    <small>{nameToLabel[item?.type]}</small>
                                    <div style={{ display: 'flex' }}>
                                      <div
                                        className={styles.dragItem2}
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        style={provided.draggableProps.style}
                                      >
                                        <small>{nameToIcon[item?.type]}</small>
                                        <p className={styles.itemText}>
                                          {item.value || item.name}
                                        </p>
                                      </div>
                                      <div
                                        className={styles.dragHandle}
                                        {...provided.dragHandleProps}
                                        isDragging={snapshot.isDragging}
                                      >
                                        <button
                                          onClick={() => updateFieldType(item)}
                                        >
                                          <SettingsIcon
                                            className={styles.dragIconTop}
                                          />
                                        </button>
                                        <MoveIcon className={styles.moveIcon} />
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ))
                          ) : (
                            <div className={styles.empty}>
                              <EmptyChartIcon />
                              <h3>No forms here yet</h3>
                              <p>
                                Your form is blank, drag and drop new form
                                fields from the left sidebar to start building a
                                new form here
                              </p>
                            </div>
                          )}

                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div
            className={cx(styles.columnCreate, styles.rightCreate, styles.pad)}
          >
            <p className={styles.description}>
              Here is a preview of the product you are creating
            </p>
            <PhonePreview title={data.title}>
              {Object.keys(state).map((list) => {
                return (
                  <div className={styles.preview}>
                    {state[list] && state[list].length
                      ? state[list].map((item, index) => (
                          <div className={styles.previewgroup}>
                            <p className={styles.previewlabel}>
                              {item.value || item.name}
                            </p>
                            <div className={styles.previewinput}>
                              {item.hint}
                            </div>
                          </div>
                        ))
                      : null}
                  </div>
                );
              })}
            </PhonePreview>
          </div>
        </div>
      </DragDropContext>
      <FormBuilderSettings
        drawerActive={fieldEditDrawer}
        setDrawerActive={setFieldEditDrawer}
        currentFiedType={currentFiedType}
        updateFieldTypeItem={updateFieldTypeItem}
        handleDeleteField={handleDeleteField}
        isOnboarding={false}
      />
    </div>
  );
};

export default StepFour;
